import type { CategorySelection, ItemCategory } from "@/lib/types";
import { categoryLabels } from "@/lib/categoryLabels";
import type { CategoryStructure } from "@/lib/categoryStructure";
import type { PriceRange } from "@/lib/market/types";

/**
 * Single owner of market browse URL <-> state serialization.
 *
 * These are pure functions over a query string. The commit method
 * (router.push vs history.replaceState) stays in `useMarketBrowse` so Phase 1
 * preserves the exact current navigation behavior per parameter:
 *   - search / location / category => router.push (navigation)
 *   - sort / price                 => history.replaceState (no navigation)
 * Normalizing these is deferred to a later phase.
 */

export function serializeLocationParam(
  current: string,
  location: string,
): string {
  const params = new URLSearchParams(current);
  if (location) {
    params.set("location", location);
  } else {
    params.delete("location");
  }
  return params.toString();
}

export function serializeCategoryParams(
  current: string,
  category: CategorySelection | null,
): string {
  const params = new URLSearchParams(current);
  if (!category) {
    params.delete("category");
    params.delete("subcategory");
  } else {
    params.set("category", category.categoryId);
    if (category.subcategoryId) {
      params.set("subcategory", category.subcategoryId);
    } else {
      // Clearing a subcategory must drop the param (not leave a stale value).
      params.delete("subcategory");
    }
  }
  return params.toString();
}

/**
 * Chip-rail toggle semantics for the market category surface.
 *
 * - Re-tap the active top-level category → clear category filter
 * - Re-tap the active subcategory → keep category, clear subcategory
 * - Otherwise apply `next` as-is (including `null` from "All Categories")
 */
export function resolveNextCategorySelection(
  current: CategorySelection | null,
  next: CategorySelection | null,
): CategorySelection | null {
  if (!next) return null;

  const sameCategory = current?.categoryId === next.categoryId;
  if (!sameCategory) return next;

  // Top-level chip re-tap (next has no subcategory).
  if (!next.subcategoryId) {
    return null;
  }

  // Subcategory chip re-tap → stay in category, drop refine.
  if (current?.subcategoryId === next.subcategoryId) {
    return {
      categoryId: next.categoryId,
      subcategoryId: "",
      categoryName: next.categoryName,
      subcategoryName: "",
    };
  }

  return next;
}

export function serializeSortParam(current: string, sort: string): string {
  const params = new URLSearchParams(current);
  if (sort === "proximity") {
    params.delete("sort");
  } else {
    params.set("sort", sort);
  }
  return params.toString();
}

export function serializePriceParams(
  current: string,
  range: PriceRange,
): string {
  const params = new URLSearchParams(current);
  if (range.min) {
    params.set("min_price", range.min);
  } else {
    params.delete("min_price");
  }
  if (range.max) {
    params.set("max_price", range.max);
  } else {
    params.delete("max_price");
  }
  return params.toString();
}

export function serializeSearchParam(current: string, query: string): string {
  const params = new URLSearchParams(current);
  if (query.trim()) {
    params.set("search", query);
  } else {
    params.delete("search");
  }
  return params.toString();
}

/**
 * Clears every browse filter param (search, category, subcategory, location,
 * price) while preserving non-filter params such as `sort`. Single owner for
 * the "Clear all filters" action (Phase 4, D4).
 */
export function serializeClearAllFilters(current: string): string {
  const params = new URLSearchParams(current);
  params.delete("search");
  params.delete("category");
  params.delete("subcategory");
  params.delete("location");
  params.delete("min_price");
  params.delete("max_price");
  return params.toString();
}

export type MarketFilterChipKind =
  | "search"
  | "category"
  | "subcategory"
  | "location"
  | "price";

export interface MarketFilterChip {
  kind: MarketFilterChipKind;
  label: string;
}

/** Compact Naira label for chip display, e.g. 50000 -> "₦50k", 1500000 -> "₦1.5m". */
function formatCompactNaira(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `₦${Number.isInteger(m) ? m : Number(m.toFixed(1))}m`;
  }
  if (value >= 1_000) {
    const k = value / 1_000;
    return `₦${Number.isInteger(k) ? k : Number(k.toFixed(1))}k`;
  }
  return `₦${value}`;
}

/** Builds the price chip label from a range, or "" when no price filter is set. */
export function formatPriceRangeLabel(range: PriceRange): string {
  const min = range.min.trim() ? parseInt(range.min, 10) : null;
  const max = range.max.trim() ? parseInt(range.max, 10) : null;
  const hasMin = min !== null && !Number.isNaN(min);
  const hasMax = max !== null && !Number.isNaN(max);
  if (hasMin && hasMax)
    return `${formatCompactNaira(min!)}–${formatCompactNaira(max!)}`;
  if (hasMin) return `Over ${formatCompactNaira(min!)}`;
  if (hasMax) return `Under ${formatCompactNaira(max!)}`;
  return "";
}

/**
 * Derives the ordered list of active-filter chips from browse state (Phase 4).
 * Pure so the chip row stays presentation-only and this stays unit-testable.
 *
 * Category / subcategory are owned by `MarketCategorySelector` (nested rail +
 * highlight), so they are intentionally omitted here to avoid duplicate chrome.
 */
export function buildActiveFilterChips(input: {
  searchQuery: string;
  selectedCategory: CategorySelection | null;
  selectedLocation: string;
  priceRange: PriceRange;
}): MarketFilterChip[] {
  const chips: MarketFilterChip[] = [];

  const query = input.searchQuery.trim();
  if (query) {
    chips.push({ kind: "search", label: `"${query}"` });
  }

  // Category/subcategory intentionally omitted — selector owns that state.

  const location = input.selectedLocation.trim();
  if (location) {
    chips.push({ kind: "location", label: location });
  }

  const priceLabel = formatPriceRangeLabel(input.priceRange);
  if (priceLabel) {
    chips.push({ kind: "price", label: priceLabel });
  }

  return chips;
}

/**
 * Ensures the selected top-level category appears in the chip rail even when
 * it was chosen via "More" and is outside the quick-pick window.
 */
export function buildVisibleCategoryChips(
  quickPicks: CategorySelection[],
  selectedCategory: CategorySelection | null,
): CategorySelection[] {
  if (!selectedCategory) return quickPicks;
  if (quickPicks.some((p) => p.categoryId === selectedCategory.categoryId)) {
    return quickPicks;
  }
  return [
    {
      categoryId: selectedCategory.categoryId,
      subcategoryId: "",
      categoryName: selectedCategory.categoryName,
      subcategoryName: "",
    },
    ...quickPicks,
  ];
}

/** Default number of category quick-picks surfaced on browse (Phase 6). */
export const CATEGORY_QUICK_PICK_LIMIT = 8;

/**
 * Derives the category quick-picks from the active category structure (Phase 6).
 * v1 has no per-category popularity signal on the client, so order = the first N
 * admin-activated categories in configured order. Sourcing from
 * `activeCategoryStructure` (never hardcoded ids) keeps the list in sync as
 * admins enable/disable categories.
 *
 * Each pick is a top-level `CategorySelection` (no subcategory) so tapping it
 * flows through the existing `serializeCategoryParams` / `handleCategorySelect`
 * owner — no parallel category-selection path.
 */
export function buildCategoryQuickPicks(
  activeCategoryStructure: CategoryStructure[],
  limit: number = CATEGORY_QUICK_PICK_LIMIT,
): CategorySelection[] {
  return activeCategoryStructure.slice(0, limit).map((category) => ({
    categoryId: category.id,
    subcategoryId: "",
    categoryName: category.name,
    subcategoryName: "",
  }));
}

/**
 * Derives subcategory drill-down picks for the selected top-level category
 * (Phase 6, consolidated category surface). Returns `[]` when no category is
 * selected or the selection isn't in the active structure (e.g. a stale/disabled
 * category id). Each pick is a full `CategorySelection` carrying the parent
 * category, so tapping it flows through the same `handleCategorySelect` owner —
 * no parallel subcategory-selection path.
 */
export function buildSubcategoryPicks(
  activeCategoryStructure: CategoryStructure[],
  selectedCategory: CategorySelection | null,
): CategorySelection[] {
  if (!selectedCategory) return [];
  const category = activeCategoryStructure.find(
    (c) => c.id === selectedCategory.categoryId,
  );
  if (!category) return [];
  return category.subcategories.map((subcategory) => ({
    categoryId: category.id,
    subcategoryId: subcategory.id,
    categoryName: category.name,
    subcategoryName: subcategory.name,
  }));
}

export interface InitialMarketState {
  location: string | null;
  category: CategorySelection | null;
  search: string | null;
  sort: string | null;
  priceRange: PriceRange | null;
}

/**
 * Parses initial browse state from URL params. Returns `null` for any field not
 * present/valid so callers only apply what the URL actually contained (mirrors
 * the current one-time init behavior).
 */
export function readInitialMarketState(
  params: URLSearchParams,
  activeCategoryStructure: CategoryStructure[],
): InitialMarketState {
  const location = params.get("location") || null;

  let category: CategorySelection | null = null;
  const categoryParam = params.get("category") as ItemCategory | null;
  if (categoryParam && Object.keys(categoryLabels).includes(categoryParam)) {
    const subcategoryParam = params.get("subcategory");
    const categoryStructureMatch = activeCategoryStructure.find(
      (c) => c.id === categoryParam,
    );
    const subcategoryMatch =
      subcategoryParam && categoryStructureMatch
        ? categoryStructureMatch.subcategories.find(
            (s) => s.id === subcategoryParam,
          )
        : undefined;

    category = {
      categoryId: categoryParam,
      subcategoryId: subcategoryMatch ? (subcategoryParam ?? "") : "",
      categoryName: categoryLabels[categoryParam],
      subcategoryName: subcategoryMatch?.name ?? "",
    };
  }

  const search = params.get("search") || null;
  const sort = params.get("sort") || null;

  const minPrice = params.get("min_price");
  const maxPrice = params.get("max_price");
  const priceRange =
    minPrice || maxPrice ? { min: minPrice || "", max: maxPrice || "" } : null;

  return { location, category, search, sort, priceRange };
}
